// This must always be the first statement in the entrypoint!
import { QFood, globalAxios } from "@q-food/q-food";

if (import.meta.env.VITE_E2E === "true") {
	// The E2E app is served single-origin on a random port (one per parallel test fork), so the API
	// host cannot be baked in — a fixed one would point every fork's browser at whichever fork happened
	// to claim that port. Same-origin is correct by construction, and keeps the scheme http, which the
	// production fallback below does not.
	QFood.BASE_URL = window.location.origin;
} else if (import.meta.env.VITE_QFOOD_API_HOST === undefined || import.meta.env.VITE_QFOOD_API_HOST === "") {
	QFood.BASE_URL = `https://${window.location.host}`;
} else {
	QFood.BASE_URL = import.meta.env.VITE_QFOOD_API_HOST;
}

import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import "./styles/main.css";

import { FontAwesomeLayersText } from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/vue";
import axios, { AxiosError } from "axios";
import { createApp } from "vue";
import acl from "~/plugins/Acl.ts";
import i18n from "~/plugins/I18n.ts";
import NumberFormat from "~/plugins/NumberFormat.ts";
import App from "./App.vue";
import AppDataGridBooleanCellRenderer from "./components/AppDataGridBooleanCellRenderer.vue";
import AppActiveToggleCellRenderer from "./components/AppActiveToggleCellRenderer.vue";
import AppDataGridFilterPresetsToolPanel from "./components/AppDataGridFilterPresetsToolPanel.vue";
import AppDataGridStatusbar from "./components/AppDataGridStatusbar.vue";
import { FloatingVue } from "./plugins/FloatingVue";
import { FontAwesomeIcon, FontAwesomeLayers } from "./plugins/FontAwesome";
import VueRouter from "./plugins/VueRouter";
import useRouter from "./composables/useRouter";
import { useAppTabs } from "./stores/useAppTabs";
import { formKit, formKitConfig } from "./plugins/formKit/FormKit";

const { error } = useToast();
let currentEtag: string | null = null;

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule.with(AgChartsEnterpriseModule)]);
LicenseManager.setLicenseKey(
	"Using_this_{AG_Grid}_Enterprise_key_{AG-088406}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Quotec}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{19_May_2026}____[v3]_[01]_MTc3OTE0NTIwMDAwMA==cb7c8465790fc7d3c54e38a4c4206dc7",
);

if (process.env.NODE_ENV != "development") {
	setInterval(() => {
		axios
			.get("")
			.then((response) => {
				if (response.headers.etag != currentEtag && currentEtag !== null) {
					const updateAvailableDiv = document.getElementById("updateAvailable");
					updateAvailableDiv?.removeAttribute("hidden");
				}
				currentEtag = response.headers.etag;
			})
			.catch((error) => {
				console.log(error);
			});
	}, 60 * 1000);
}

globalAxios.defaults.baseURL = QFood.BASE_URL;

globalAxios.interceptors.request.use(
	(req) => {
		const qfoodSessionToken = localStorage.getItem("qfood-session-token");
		if (qfoodSessionToken != null) {
			req.headers.set("X-Auth-Token", qfoodSessionToken);
		}
		return req;
	},
	(error) => error,
);

import { startWebSocket, watchBridgeExpectation } from "~utils/BridgeIntegration.ts";
startWebSocket();

// E2E-only (see the window.__e2e block at the bottom of this file): record failed API calls so a test
// can assert a view loaded cleanly instead of inferring it from whatever happens to be on screen.
// Registered here, ahead of the app's own response interceptor, so it sees the raw AxiosError — that
// interceptor rewrites rejections into strings/objects that no longer carry the URL or status.
const e2eHttpFailures: { url: string; status: number | undefined; method: string | undefined }[] = [];

if (import.meta.env.VITE_E2E === "true") {
	globalAxios.interceptors.response.use(
		(res) => res,
		(responseError: AxiosError) => {
			e2eHttpFailures.push({
				url: responseError.config?.url ?? responseError.request?.responseURL ?? "unknown",
				status: responseError.response?.status,
				method: responseError.config?.method,
			});
			return Promise.reject(responseError);
		},
	);
}

globalAxios.interceptors.response.use(
	(res) => {
		return res;
	},
	async (responseError: AxiosError) => {
		let responseData = responseError.response?.data as Record<string, unknown> | string | undefined;

		if (responseData instanceof Blob) {
			responseData = JSON.parse(await responseData.text());
		}

		if (
			responseError.response?.status === 403 &&
			responseError.request?.responseURL.endsWith("/api/myself/session") === false
		) {
			error("U heeft niet genoeg permissies");
			console.log("Invalid permissions", responseError.request.responseURL);
			return Promise.reject("Invalid permissions");
		}

		if (responseData !== undefined && typeof responseData !== "string" && "localizedMessage" in responseData) {
			return Promise.reject(
				Object.assign({}, responseData, { status: responseError.status, message: responseData.localizedMessage }),
			);
		}

		return Promise.reject(responseError);
	},
);

const vueApp = createApp(App)
	.use(i18n)
	.use(acl)
	.use(NumberFormat)
	.use(createPinia())
	.use(formKit, formKitConfig)
	.use(FloatingVue)
	.use(VueRouter)
	.component("AppIcon", FontAwesomeIcon)
	.component("AppIconLayers", FontAwesomeLayers)
	.component("AppIconLayersText", FontAwesomeLayersText)
	/**
	 * By not importing these, HMR won't work on these components within AG-Grid.
	 */
	.component("AppDataGridFilterPresetsToolPanel", AppDataGridFilterPresetsToolPanel)
	.component("AppDataGridBooleanCellRenderer", AppDataGridBooleanCellRenderer)
	.component("AppActiveToggleCellRenderer", AppActiveToggleCellRenderer)
	.component("AppDataGridStatusbar", AppDataGridStatusbar);

if (import.meta.env.VITE_SENTRY_DSN !== "" && import.meta.env.VITE_SENTRY_DSN !== undefined) {
	Sentry.init({
		app: vueApp,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
				mask: [],
				unmask: ["label", "button", "h1", "h2", "h3", "h4", "h5", '[role="alert"]'],
				maskFn: (e) => e,
			}),
		],
		sendDefaultPii: true,
		// Session Replay
		replaysSessionSampleRate: 0.05, // This sets the sample rate at 5%.
		replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
	});
}

vueApp.mount("#app").$nextTick(() => {
	if ("electron" in window) {
		window.electron.removeLoading();
	}
});

// Pinia is installed now, so the user store is readable: once the logged-in user has a serial
// scale/scanner configured, mark the bridge as expected (see watchBridgeExpectation).
watchBridgeExpectation();

// E2E-only hook (baked into the `--mode=e2e` build via VITE_E2E): opens a workspace tab the same way a
// user click does — `useRouter().push()` resolves the route's props/title and calls `addTab`. Lets the
// browser E2E DSL open forms/grids, which plain URL navigation no longer does (the auto-tab router
// guard is disabled). Runs inside the app's injection context so router + pinia are available.
//
// It also reports the app's own errors, which is the whole point of the smoke sweep: a view that
// throws during setup still leaves a plausible-looking shell on screen, so "did anything break" is a
// question only the app can answer honestly. `apiBaseUrl` is here so a test can verify it is driving
// an --mode=e2e bundle at all — getting that wrong bakes localhost:8080 as the API host and fails
// every spec 10s at a time with an unrelated-looking timeout.
if (import.meta.env.VITE_E2E === "true") {
	const e2eErrors: string[] = [];
	// Plain objects are common here (the axios interceptors reject with them), and String() turns those
	// into a useless "[object Object]" — so serialise instead.
	const describe = (reason: unknown) => {
		if (reason instanceof Error) return `${reason.name}: ${reason.message}\n${reason.stack ?? ""}`;
		if (typeof reason === "object" && reason !== null) {
			try {
				return JSON.stringify(reason);
			} catch {
				return Object.prototype.toString.call(reason);
			}
		}
		return String(reason);
	};

	window.addEventListener("error", (event) => e2eErrors.push(describe(event.error ?? event.message)));
	window.addEventListener("unhandledrejection", (event) => e2eErrors.push(describe(event.reason)));

	// console.error too, chained so the devtools output is unchanged. Not every failure throws: ag-grid
	// and several components report a broken state this way (there are ~25 call sites, one of them the
	// AppDataGrid catch that the 2026-08-25 "Fix some console errors for grid" hotfix was about). Without
	// this the smoke sweep would have watched that regression go past.
	const previousConsoleError = console.error;
	console.error = (...args: unknown[]) => {
		e2eErrors.push(`console.error: ${args.map(describe).join(" ")}`);
		previousConsoleError(...args);
	};

	// Chained, not replaced: Sentry installs its own handler above and must keep receiving errors.
	const previousErrorHandler = vueApp.config.errorHandler;
	vueApp.config.errorHandler = (err, instance, info) => {
		e2eErrors.push(`${describe(err)} (vue: ${info})`);
		previousErrorHandler?.(err, instance, info);
	};

	(
		window as unknown as {
			__e2e: {
				openTab: (fullPath: string, keepExistingTabs?: boolean) => void;
				openTabCount: () => number;
				apiBaseUrl: string;
				errors: string[];
				httpFailures: typeof e2eHttpFailures;
				reset: () => void;
			};
		}
	).__e2e = {
		// Closes the other tabs first by default: inactive tabs stay in the DOM (v-show), so leaving old
		// tabs open would duplicate element ids (e.g. two #tradeName inputs) and Selenium's By.id would
		// hit the hidden one. One tab per view() keeps ids unique.
		//
		// `keepExistingTabs` deliberately gives that up. Several of the worst production bugs have been
		// exactly this: a lookup that reaches across tabs and finds the wrong one — document.getElementById
		// returning the first tab's element, FormKit's registry returning the most recently registered
		// node, ag-grid's api handing back the most recently created grid. A harness that always closes
		// the other tabs can never reproduce them, so the multi-tab spec opts in and scopes its own
		// lookups to the visible tab instead.
		openTab: (fullPath: string, keepExistingTabs = false) =>
			vueApp.runWithContext(() => {
				if (!keepExistingTabs) useAppTabs().sessionTabs.value = [];
				useRouter().push(fullPath);
			}),
		openTabCount: () => vueApp.runWithContext(() => useAppTabs().sessionTabs.value.length) ?? 0,
		apiBaseUrl: QFood.BASE_URL,
		errors: e2eErrors,
		httpFailures: e2eHttpFailures,
		// Called before opening each view so a failure is attributed to the view that caused it.
		reset: () => {
			e2eErrors.length = 0;
			e2eHttpFailures.length = 0;
		},
	};
}
